<template>
  <div v-if="showBattleModal" class="battle-modal" @click="closeModal">
    <div class="battle-container" @click.stop>
      <!-- 固定头部：标题 -->
      <div class="battle-header">
        <h3>妖兽战斗</h3>
      </div>

      <!-- 并列显示的战斗状态 -->
      <div class="battle-combatants">
        <!-- 玩家状态 -->
        <div class="combatant player-combatant">
          <div class="character-info">
            <!-- 顶部：名字 -->
            <div class="name-section">
              <h4 class="character-name">{{ playerName }}</h4>
            </div>

            <!-- 中间：描述 -->
            <div class="details-section">
              <div class="player-details">
                <p class="player-description">灵力值: {{ battleState.player.mana || 0 }}</p>
                <p class="player-level">境界: {{ getPlayerRealmName(battleState.player.realm || 0) }}</p>
              </div>
            </div>

            <!-- 底部：血条和效果 -->
            <div class="health-section">
              <div class="health-bar">
                <div class="health-fill" :style="{ width: playerHealthPercent + '%' }"></div>
                <span class="health-text">{{ battleState.player.currentQi }}/{{ battleState.player.maxQi }}</span>
              </div>
              <div class="effects" v-if="battleState.player.effects.length > 0">
                <span v-for="effect in battleState.player.effects" :key="effect.type" class="effect">
                  {{ getEffectDisplay(effect) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- VS -->
        <div class="vs-container">
          <div class="vs-indicator">VS</div>
        </div>

        <!-- 妖兽状态 -->
        <div class="combatant beast-combatant">
          <div class="character-info">
            <!-- 顶部：名字 -->
            <div class="name-section">
              <h4 class="character-name">{{ battleState.beast.name }}</h4>
            </div>

            <!-- 中间：描述 -->
            <div class="details-section">
              <div class="beast-details">
                <p class="beast-description">{{ battleState.beast.description }}</p>
                <p class="beast-level">境界: {{ getBeastRealmName(battleState.beast.realm) }} 等级: {{ battleState.beast.level }}</p>
              </div>
            </div>

            <!-- 底部：血条和效果 -->
            <div class="health-section">
              <div class="health-bar">
                <div class="health-fill beast-health" :style="{ width: beastHealthPercent + '%' }"></div>
                <span class="health-text">{{ battleState.beast.currentQi }}/{{ battleState.beast.maxQi }}</span>
              </div>
              <div class="effects" v-if="battleState.beast.effects.length > 0">
                <span v-for="effect in battleState.beast.effects" :key="effect.type" class="effect">
                  {{ getEffectDisplay(effect) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 战斗日志 -->
      <div class="battle-log">
        <div class="log-content">
          <div v-for="(log, index) in battleState.log" :key="index" class="log-entry">
            {{ log.message }}
          </div>
        </div>
      </div>

      <!-- 固定底部：战斗控制或结果 -->
      <div class="battle-footer">
        <!-- 战斗控制 -->
        <div class="battle-controls" v-if="!battleState.battleResult">
          <div v-if="battleState.isPlayerTurn" class="player-turn">
            <h4>你的回合</h4>
            <div class="action-buttons">
              <button
                v-for="skill in playerSkills"
                :key="skill"
                @click="useSkill(skill)"
                class="skill-button"
              >
                {{ skill }}
              </button>
              <button @click="attemptFlee" class="flee-button">逃跑</button>
            </div>
          </div>
          <div v-else class="beast-turn">
            <p>妖兽正在行动...</p>
          </div>
        </div>

        <!-- 战斗结果 -->
        <div v-else class="battle-result">
          <h4 v-if="battleState.battleResult === 'victory'">战斗胜利！</h4>
          <h4 v-else-if="battleState.battleResult === 'defeat'">战斗失败！</h4>
          <h4 v-else-if="battleState.battleResult === 'fled'">成功逃脱！</h4>
          <button @click="closeModal" class="close-button">关闭</button>
        </div>

        <!-- 回合信息 -->
        <div class="turn-info">
          第 {{ battleState.turn }} 回合
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getBeastRealmName } from './beast-data.js';

const REALM_NAMES = ['炼气', '筑基', '金丹', '元婴', '化神', '渡劫', '真仙'];

export default {
  name: 'BattleModal',
  props: {
    showBattleModal: {
      type: Boolean,
      default: false
    },
    battleState: {
      type: Object,
      default: () => null
    },
    playerName: {
      type: String,
      default: '玩家'
    },
    playerSkills: {
      type: Array,
      default: () => ['攻击']
    }
  },
  watch: {
    battleState: {
      handler(newVal) {
        // Monitor battle state changes for UI updates
        if (newVal?.battleResult) {
          // Battle ended, component will handle result display
          this.$emit('battle-ended', newVal.battleResult);
        }
      },
      deep: true
    }
  },
  computed: {
    playerHealthPercent() {
      if (!this.battleState || !this.battleState.player) return 0;
      return (this.battleState.player.currentQi / this.battleState.player.maxQi) * 100;
    },
    beastHealthPercent() {
      if (!this.battleState || !this.battleState.beast) return 0;
      return (this.battleState.beast.currentQi / this.battleState.beast.maxQi) * 100;
    }
  },
  methods: {
    getBeastRealmName(realm) {
      return getBeastRealmName(realm);
    },
    getPlayerRealmName(realm) {
      return REALM_NAMES[realm] || '未知';
    },
    getEffectDisplay(effect) {
      const effectNames = {
        poison: '中毒',
        stun: '眩晕',
        confuse: '混乱',
        fear: '恐惧',
        petrify: '石化',
        stealth: '隐身',
        blind: '致盲'
      };
      return `${effectNames[effect.type] || effect.type}(${effect.duration})`;
    },
    useSkill(skill) {
      this.$emit('use-skill', skill);
    },
    attemptFlee() {
      this.$emit('attempt-flee');
    },
    closeModal() {
      this.$emit('close-modal');
    }
  }
};
</script>

<style scoped>
.battle-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.battle-container {
  background: #1a1a2e;
  border: 2px solid #16213e;
  border-radius: 10px;
  padding: 15px;
  max-width: 900px;
  width: 95%;
  max-height: 90vh;
  height: 85vh;
  display: flex;
  flex-direction: column;
  color: #e6e6e6;
  overflow: hidden;
}

/* 固定头部 */
.battle-header {
  flex-shrink: 0;
  text-align: center;
  padding-bottom: 15px;
  border-bottom: 1px solid #16213e;
}

.battle-header h3 {
  margin: 0;
  color: #ff6b6b;
  font-size: 18px;
}

/* 并列战斗状态 */
.battle-combatants {
  flex-shrink: 0;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  padding: 15px 0;
  gap: 15px;
}

.combatant {
  flex: 1;
  background: #0f0f23;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  height: 120px;
}

.character-info {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.name-section {
  flex-shrink: 0;
  margin-bottom: 8px;
}

.character-name {
  margin: 0;
  color: #4ecdc4;
  font-size: 16px;
}

.details-section {
  flex: 1;
  min-height: 32px;
  margin-bottom: 8px;
}

.beast-details {
  height: 100%;
}

.beast-description {
  font-size: 11px;
  color: #888;
  margin: 0 0 4px 0;
  line-height: 1.3;
}

.beast-level {
  font-size: 12px;
  color: #ffd166;
  margin: 0;
}

.player-details {
  height: 100%;
}

.player-description {
  font-size: 11px;
  color: #888;
  margin: 0 0 4px 0;
  line-height: 1.3;
}

.player-level {
  font-size: 12px;
  color: #ffd166;
  margin: 0;
}

.health-section {
  flex-shrink: 0;
}

.health-bar {
  position: relative;
  height: 20px;
  background: #333;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 8px;
}

.health-fill {
  height: 100%;
  background: linear-gradient(90deg, #ff4757, #ff3838);
  transition: width 0.3s ease;
}

.beast-health {
  background: linear-gradient(90deg, #ff6b6b, #ee5a52);
}

.health-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-weight: bold;
  font-size: 11px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.7);
}

.effects {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}

.effect {
  background: #ff3838;
  color: white;
  padding: 2px 5px;
  border-radius: 8px;
  font-size: 11px;
}

.vs-container {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.vs-indicator {
  font-size: 20px;
  font-weight: bold;
  color: #ffd166;
  background: #16213e;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 战斗日志 */
.battle-log {
  flex: 1;
  background: #0f0f23;
  border-radius: 8px;
  padding: 12px;
  margin: 10px 0;
  overflow-y: auto;
  min-height: 100px;
}

.log-content {
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.4;
}

.log-entry {
  margin-bottom: 4px;
  padding: 2px 0;
}

/* 固定底部 */
.battle-footer {
  flex-shrink: 0;
  border-top: 1px solid #16213e;
  padding-top: 15px;
}

.battle-controls {
  background: #0f0f23;
  border-radius: 8px;
  padding: 12px;
}

.player-turn h4 {
  margin: 0 0 8px 0;
  color: #4ecdc4;
  font-size: 16px;
}

.beast-turn p {
  margin: 0;
  color: #ffd166;
  text-align: center;
  font-size: 14px;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}

.skill-button {
  background: #4ecdc4;
  color: #0f0f23;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  font-size: 14px;
  transition: background 0.2s;
  min-width: 80px;
}

.skill-button:hover {
  background: #45b7aa;
}

.flee-button {
  background: #ffd166;
  color: #0f0f23;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  font-size: 14px;
  transition: background 0.2s;
  min-width: 80px;
}

.flee-button:hover {
  background: #e6c15b;
}

.battle-result {
  text-align: center;
  background: #0f0f23;
  border-radius: 8px;
  padding: 15px;
}

.battle-result h4 {
  margin: 0 0 15px 0;
  color: #4ecdc4;
  font-size: 16px;
}

.close-button {
  background: #ff6b6b;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  font-size: 14px;
  transition: background 0.2s;
}

.close-button:hover {
  background: #ff5252;
}

.turn-info {
  text-align: center;
  color: #888;
  font-size: 13px;
  margin-top: 8px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .battle-container {
    width: 98%;
    height: 95vh;
    padding: 10px;
  }

  .battle-combatants {
    gap: 10px;
    padding: 10px 0;
  }

  .combatant {
    padding: 10px;
    min-height: 100px;
  }

  .character-name {
    font-size: 14px;
  }

  .beast-description {
    font-size: 10px;
  }

  .beast-level {
    font-size: 11px;
  }

  .vs-indicator {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .battle-log {
    padding: 10px;
    min-height: 80px;
  }

  .log-content {
    font-size: 12px;
  }

  .battle-footer {
    padding-top: 10px;
  }

  .battle-controls {
    padding: 10px;
  }

  .action-buttons {
    gap: 6px;
  }

  .skill-button,
  .flee-button {
    padding: 6px 12px;
    font-size: 13px;
    min-width: 70px;
  }
}
</style>
